import { useEffect, useState, useContext, FC, useMemo } from 'react';
import { UserContext } from '../../../context';
import {
  Button as SMButton,
  Loader,
  Page,
  PageTitleWithBack,
  SMBSelect,
  BuildNotes
} from '../../../components';
import { Prompt, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  IBuildTenDayDetails,
  UserRoles,
  IDropdownResponse,
  buildDocuments,
  ISalesIncentive
} from '../../../models';
import {
  approveBuild,
  getAddendumG,
  getBuildTenDayDetails,
  getFinalPriceContract,
  updateBuildTenDayLockoutDate,
  updateContractAdjustment,
  regenerateTenDayAcceptanceDocs
} from '../../../fetch';
import { getIncentiveTypes } from '../../../fetch/lookups/sales-portal';
import { useQuery } from 'react-query';
import {
  CheckBox,
  Download,
  Info,
  Save,
  RestorePage,
  HistoryEdu,
  Close
} from '@mui/icons-material';
import {
  Box,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
  Button,
  useMediaQuery,
  Autocomplete,
  MenuItem
} from '@mui/material';
import { deepEqual } from 'fast-equals';
import { downloadFile, formatMoney, getDownloadFileName } from '../../../helpers';
import { useSnackbar } from 'notistack';
import { theme } from '../../../styles/theme';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { NumericFormat } from 'react-number-format';
import { useHistory } from 'react-router-dom';
import { useFlags } from '../../../hooks';

const BuildDetailsSchema = Yup.object().shape({
  lockoutDate: Yup.string()
});

interface ITenDayBuildsDetail {
  backText?: string;
  backUrl?: string;
}
export const TenDayBuildsDetail: FC<ITenDayBuildsDetail> = ({
  backText = 'Back to Ten Day Builds',
  backUrl = '/sales/contracting/ten-day-builds'
}) => {
  const classes = useStyles();
  const { user } = useContext(UserContext);
  const {
    salesIncentiveRepeater: salesIncentiveRepeaterFF,
    lotPremiumOptionDepositCalculation: lotPremiumOptionDepositCalculationFF
  } = useFlags();
  const { enqueueSnackbar } = useSnackbar();
  const { buildId } = useParams<{ buildId: string }>();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const isPhone = useMediaQuery('(max-width: 600px)');
  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get('redirect');

  const allowedToViewApproveBuild =
    user?.userRoles?.includes(UserRoles.BLAdmin) ||
    user?.userRoles?.includes(UserRoles.BLSalesAdmin) ||
    user?.userRoles?.includes(UserRoles.BLSalesCoordinator);

  const isAllowedToEditAndSavePrices =
    user?.userRoles?.includes(UserRoles.BLAdmin) ||
    user?.userRoles?.includes(UserRoles.BLSalesCoordinator) ||
    user?.userRoles?.includes(UserRoles.BLClosing);

  const [isLoadingBuildDetails, setIsLoadingBuildDetails] = useState<boolean>(false);
  const [isApprovingBuild, setIsApprovingBuild] = useState<boolean>(false);
  const [isLoadingAddendumG, setIsLoadingAddendumG] = useState<boolean>(false);
  const [isLoadingFinalPriceContract, setIsLoadingFinalPriceContract] = useState<boolean>(false);
  const [isUpdatingLockoutDate, setIsUpdatingLockoutDate] = useState<boolean>(false);
  const [buildDetails, setBuildDetails] = useState<IBuildTenDayDetails | null>(null);
  const [isUpdatingContractAdjustment, setUpdatingContractAdjustment] = useState<boolean>(false);
  const [isDirty, setDirty] = useState<boolean>(false);
  const [basePriceAdjustment, setBasePriceAdjustment] = useState<number | string>('');
  const [basePriceAdjusted, setBasePriceAdjusted] = useState<number | string>('');
  const [lotPremiumAdjustment, setLotPremiumAdjustment] = useState<number | string>('');
  const [lotPremiumAdjusted, setLotPremiumAdjusted] = useState<number | string>('');
  const [incentiveAdjustment, setIncentiveAdjustment] = useState<number | string>('');
  const [totalPriceAdjustments, setTotalPriceAdjustments] = useState<number | string>('');
  const [optionCapOverageAdjustment, setOptionCapOverageAdjustment] = useState<number | string>('');
  const [optionCapOverageAdjusted, setOptionCapOverageAdjusted] = useState<number | string>('');
  const [reasonForAdjustment, setReasonForAdjustment] = useState<string>('');
  const [isRegenerating, setRegenerating] = useState(false);
  const [totalPrice, setTotalPrice] = useState<number | string>('');
  const [selectedIncentiveTypeValues, setSelectedIncentiveTypeValues] = useState<ISalesIncentive[]>(
    []
  );
  const [inputSelectedSurveyRequirement, setInputSelectedSurveyRequirement] = useState('');

  const {
    selectionsComplete,
    canApproveBuild,
    tenDayComplete,
    displayLockoutDate,
    buildButtonTitle
  } = useMemo(() => {
    const buildStatus = buildDetails?.buildStatus;
    const selectionsComplete = buildDetails?.selectionsComplete;
    const canApproveBuild = buildStatus === 'ContractAccepted' && selectionsComplete === true;
    const tenDayComplete = buildStatus === 'TenDayComplete';
    const displayLockoutDate = buildStatus !== 'New' && buildStatus !== 'BuildApproved';
    const buildButtonTitle =
      buildStatus === 'New' || buildStatus === 'BuildApproved' || buildStatus === 'ContractAccepted'
        ? 'Approve Build'
        : 'Build Approved';
    return {
      selectionsComplete,
      canApproveBuild,
      tenDayComplete,
      displayLockoutDate,
      buildButtonTitle
    };
  }, [buildDetails]);

  const { isLoading: isLoadingIncentiveTypes, data: incentiveTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['incentive-types'], () => getIncentiveTypes());

  const incentiveTypesData = useMemo(() => incentiveTypes ?? [], [incentiveTypes]);

  const requireIncentiveType =
    Number(incentiveAdjustment) > 0 && !(selectedIncentiveTypeValues.length > 0);

  useEffect(() => {
    const calculatedTotalPriceAdjustment =
      Number(lotPremiumAdjustment ?? 0) +
      Number(incentiveAdjustment ?? 0) +
      Number(basePriceAdjustment ?? 0);
    setTotalPriceAdjustments(calculatedTotalPriceAdjustment);
  }, [lotPremiumAdjustment, incentiveAdjustment, basePriceAdjustment]);

  const fetchBuildTenDayDetails = async () => {
    try {
      setIsLoadingBuildDetails(true);
      const res = await getBuildTenDayDetails(buildId);
      setBuildDetails(res);
      setSelectedIncentiveTypeValues(res.salesIncentives);
      setBasePriceAdjusted(res?.basePrice?.adjusted);
      setBasePriceAdjustment(res?.basePrice?.adjustment);
      setLotPremiumAdjusted(res?.lotPremium?.adjusted);
      setLotPremiumAdjustment(res?.lotPremium?.adjustment);
      setIncentiveAdjustment(res?.incentive ?? 0);
      setTotalPriceAdjustments(
        Number(res?.lotPremium?.adjustment) +
          Number(res?.incentive ?? 0) +
          Number(res?.basePrice?.adjustment)
      );
      setOptionCapOverageAdjustment(res?.upgradeCapOverage?.adjustment);
      setOptionCapOverageAdjusted(res?.upgradeCapOverage?.adjusted);
      setReasonForAdjustment(res?.contractAdjustmentReason);
      setTotalPrice(res?.totalPrice);
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error loading build details.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingBuildDetails(false);
    }
  };

  const handleLockoutDateChange = async (id: string | number, date: string) => {
    try {
      setIsUpdatingLockoutDate(true);
      const res = await updateBuildTenDayLockoutDate(id, { optionLockoutDate: date });
      if (res) {
        enqueueSnackbar(`Lockout Date updated!`, {
          variant: 'success'
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error updating lockout date.`, {
        variant: 'error'
      });
    } finally {
      setIsUpdatingLockoutDate(false);
      fetchBuildTenDayDetails();
    }
  };

  const handleApproveBuild = async () => {
    try {
      setIsApprovingBuild(true);
      const res = await approveBuild(buildId);
      if (res) {
        enqueueSnackbar(
          `Build approved! Addendum G and Updated Final Price Contract have been sent to the agent for follow up`,
          {
            variant: 'success'
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error approving build.`, {
        variant: 'error'
      });
    } finally {
      setIsApprovingBuild(false);
      fetchBuildTenDayDetails();
    }
  };

  const fetchAddendumG = async () => {
    try {
      setIsLoadingAddendumG(true);
      const res = await getAddendumG(buildId);
      downloadFile(res.data, getDownloadFileName(res) ?? 'Addendum-G.pdf');
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading Addendum G, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingAddendumG(false);
    }
  };

  const fetchFinalPriceContract = async () => {
    try {
      setIsLoadingFinalPriceContract(true);
      const res = await getFinalPriceContract(buildId);
      downloadFile(res.data, getDownloadFileName(res) ?? 'Final-Price-Contract.pdf');
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading Final Price Contract, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingFinalPriceContract(false);
    }
  };

  const regenerateDocs = async () => {
    setRegenerating(true);
    try {
      await regenerateTenDayAcceptanceDocs(Number(buildId), false);
      enqueueSnackbar(`Documents successfully regenerated!`, {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error regenerating documents, please try again.', {
        variant: 'error'
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleContractAdjustmentUpdates = async () => {
    if (selectedIncentiveTypeValues.some(incentive => incentive.incentiveId === 0)) {
      enqueueSnackbar(`Please select an incentive type.`, {
        variant: 'error'
      });
      return;
    }

    try {
      setUpdatingContractAdjustment(true);
      const contractData = {
        basePrice: {
          defaultValue: 0,
          adjusted: basePriceAdjusted ? basePriceAdjusted : 0,
          adjustment: basePriceAdjustment ? basePriceAdjustment : 0
        },
        lotPremium: {
          defaultValue: 0,
          adjusted: lotPremiumAdjusted ? lotPremiumAdjusted : 0,
          adjustment: lotPremiumAdjustment ? lotPremiumAdjustment : 0
        },
        optionCapOverage: {
          defaultValue: 0,
          adjusted: optionCapOverageAdjusted ? optionCapOverageAdjusted : 0,
          adjustment: optionCapOverageAdjustment ? optionCapOverageAdjustment : 0
        },
        incentive: incentiveAdjustment ? Number(incentiveAdjustment) : 0,
        contractAdjustmentReason: reasonForAdjustment,
        incentiveTypes: selectedIncentiveTypeValues
      };
      await updateContractAdjustment(buildId, contractData);
      setUpdatingContractAdjustment(false);
      setDirty(false);
      fetchBuildTenDayDetails();
      enqueueSnackbar(`Contract adjustment updated!`, {
        variant: 'success'
      });
    } catch (error) {
      setUpdatingContractAdjustment(false);
      enqueueSnackbar(error?.Detail || `Error updating contract adjustment.`, {
        variant: 'error'
      });
    }
  };

  const handleAdjusted = (defaultPrice, fieldAmount) => {
    return defaultPrice + fieldAmount;
  };

  useEffect(() => {
    fetchBuildTenDayDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotPremiumOptionDepositCalculationFF]);

  const selectionDetailsLink = `/sales/contracting/build-options/${buildId}${
    redirect ? `?redirect=${redirect}` : ''
  }`;
  return (
    <Page title="Build Details">
      <PageTitleWithBack title={`Build Details`} backUrl={backUrl} backText={backText} />
      {isLoadingBuildDetails && <Loader title="Loading..." position="centered" />}
      {!isLoadingBuildDetails && (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4}>
              <Item label="Neighborhood" value={buildDetails?.neighborhoodDescription} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Item label="Lot Number" value={buildDetails?.lotNumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Item label="Plan" value={buildDetails?.planDescription} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Item label="Purchaser" value={buildDetails?.clientName} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Item
                label="Selections Complete"
                value={buildDetails?.selectionsComplete ? 'True' : 'False'}
              />
            </Grid>
          </Grid>
          <Box mb={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{
                lockoutDate: buildDetails?.optionLockoutDate
                  ? new Date(buildDetails?.optionLockoutDate)
                  : ''
              }}
              validationSchema={BuildDetailsSchema}
              onSubmit={async (values, actions) => {}}
            >
              {({
                isSubmitting,
                values,
                initialValues,
                setFieldValue,
                handleSubmit,
                dirty,
                handleBlur,
                errors,
                touched
              }) => {
                return (
                  <>
                    <Prompt
                      when={!deepEqual(initialValues, values) ? true : false}
                      message="Are you sure you want to navigate away from this page? Your unsaved changes will be lost."
                    />
                    <Form onSubmit={handleSubmit} autoComplete="none">
                      <Grid container spacing={1} marginTop={'1rem'}>
                        {displayLockoutDate && (
                          <>
                            <Grid item>
                              <DatePicker
                                disabled={!allowedToViewApproveBuild}
                                label="Lockout Date"
                                onChange={(date: any) => {
                                  setFieldValue('lockoutDate', date);
                                }}
                                value={values.lockoutDate}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    required: true,
                                    value: values.lockoutDate
                                  }
                                }}
                              />
                            </Grid>
                            {allowedToViewApproveBuild && (
                              <Grid item>
                                <LoadingButton
                                  loading={isUpdatingLockoutDate}
                                  loadingPosition="start"
                                  variant="contained"
                                  color="secondary"
                                  disabled={isLoadingBuildDetails}
                                  startIcon={<Save />}
                                  onClick={() => {
                                    handleLockoutDateChange(
                                      buildId,
                                      values.lockoutDate.toLocaleString()
                                    );
                                  }}
                                >
                                  Update
                                </LoadingButton>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </Box>

          <Typography color="primary" variant="h5" mb={0.25} mt={1}>
            Build Price Details
          </Typography>
          <Divider />
          {!isMobile && (
            <Grid container spacing={1} mt={0.1}>
              <Grid item xs={12} sm={2}>
                <Typography></Typography>
              </Grid>
              <Grid item xs={12} sm={2} className={classes.columnHeader}>
                <Box display="flex" justifyContent="end">
                  <Typography fontWeight="bold">Original</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentsHeader}>
                <Box display="flex" justifyContent="center">
                  <Typography fontWeight="bold">Adjustments/Incentives</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box display="flex" justifyContent="flex-end">
                  <Typography fontWeight="bold">Total</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
          <Grid container className={classes.buildPriceDetails}>
            <Grid container spacing={1} mt={0.1}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Base Price"
                  value={formatMoney(buildDetails?.basePrice?.defaultValue)}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                <NumericFormat
                  disabled={!isAllowedToEditAndSavePrices}
                  valueIsNumericString
                  thousandSeparator
                  customInput={TextField}
                  size="small"
                  variant="standard"
                  onValueChange={value => setBasePriceAdjustment(value.value)}
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  value={basePriceAdjustment}
                  onBlur={e => {
                    setDirty(true);
                    setBasePriceAdjusted(
                      handleAdjusted(
                        buildDetails?.basePrice?.defaultValue,
                        Number(basePriceAdjustment)
                      )
                    );
                  }}
                  style={{ backgroundColor: 'transparent' }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustedFields}>
                <Item label="" value={formatMoney(basePriceAdjusted)} bold={isPhone} />
              </Grid>
            </Grid>
            <Grid container spacing={1} mt={0.25}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Lot Premium"
                  value={formatMoney(buildDetails?.lotPremium?.defaultValue)}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                <NumericFormat
                  disabled={!isAllowedToEditAndSavePrices}
                  valueIsNumericString
                  thousandSeparator
                  customInput={TextField}
                  size="small"
                  variant="standard"
                  onValueChange={value => setLotPremiumAdjustment(value.value)}
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  value={lotPremiumAdjustment}
                  onBlur={e => {
                    setDirty(true);
                    setLotPremiumAdjusted(
                      handleAdjusted(
                        buildDetails?.lotPremium?.defaultValue,
                        Number(lotPremiumAdjustment)
                      )
                    );
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustedFields}>
                <Item label="" value={formatMoney(lotPremiumAdjusted)} bold={isPhone} />
              </Grid>
            </Grid>
            <Grid container spacing={1} mt={0.25}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Options at Contract"
                  value={formatMoney(buildDetails?.optionsAtContract)}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.noAdjustments}>
                <Item
                  label=""
                  value={formatMoney(buildDetails?.optionsAtContract)}
                  bold={isPhone}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Ten Day Options"
                  value={formatMoney(buildDetails?.tenDayUpgradeAmount)}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.noAdjustments}>
                <Item
                  label=""
                  value={formatMoney(buildDetails?.tenDayUpgradeAmount)}
                  bold={isPhone}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Total Options"
                  value={formatMoney(
                    (buildDetails?.tenDayUpgradeAmount ?? 0) +
                      (buildDetails?.optionsAtContract ?? 0)
                  )}
                  color="black"
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.noAdjustments}>
                <Item
                  label=""
                  value={formatMoney(
                    (buildDetails?.tenDayUpgradeAmount ?? 0) +
                      (buildDetails?.optionsAtContract ?? 0)
                  )}
                  bold={isPhone}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} mt={0.25}>
              <Grid item xs={12} sm={4}>
                <Item label="Incentives" value="" />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                <NumericFormat
                  disabled={!isAllowedToEditAndSavePrices}
                  valueIsNumericString
                  thousandSeparator
                  customInput={TextField}
                  size="small"
                  variant="standard"
                  onValueChange={value => setIncentiveAdjustment(value.value)}
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  value={incentiveAdjustment}
                  onBlur={e => {
                    setDirty(true);
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustedFields}>
                <Item label="" value={formatMoney(incentiveAdjustment)} bold={isPhone} />
              </Grid>
            </Grid>
            <Grid container spacing={1} mt={0.1}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Total Price"
                  value={formatMoney(Math.ceil(Number(totalPrice ?? 0)))}
                  color="black"
                  bold
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                <NumericFormat
                  disabled
                  valueIsNumericString
                  thousandSeparator
                  customInput={TextField}
                  size="small"
                  variant="standard"
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  value={totalPriceAdjustments}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustedFields}>
                <Item
                  bold
                  label=""
                  value={formatMoney(
                    Math.ceil(Number(totalPrice ?? 0) + Number(totalPriceAdjustments ?? 0))
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={12} sm={4}>
                <Item
                  label="Options Cap"
                  value={formatMoney(buildDetails?.appraisalAlarmValue)}
                  color={theme.palette.error.main}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Item
                  label={
                    lotPremiumOptionDepositCalculationFF
                      ? 'Total Options'
                      : 'Total Options/Lot Premium'
                  }
                  value={
                    lotPremiumOptionDepositCalculationFF
                      ? formatMoney(
                          (buildDetails?.tenDayUpgradeAmount ?? 0) +
                            (buildDetails?.optionsAtContract ?? 0)
                        )
                      : formatMoney(
                          Number(lotPremiumAdjusted) +
                            (buildDetails?.tenDayUpgradeAmount ?? 0) +
                            (buildDetails?.optionsAtContract ?? 0)
                        )
                  }
                  color={theme.palette.error.main}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} mb={isPhone ? 0 : 1}>
                <Item
                  label="Options Cap Overage"
                  value={formatMoney(buildDetails?.upgradeCapOverage?.defaultValue)}
                  color={theme.palette.error.main}
                />
              </Grid>
              <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                <NumericFormat
                  disabled={!isAllowedToEditAndSavePrices}
                  valueIsNumericString
                  thousandSeparator
                  customInput={TextField}
                  size="small"
                  variant="standard"
                  onValueChange={value => setOptionCapOverageAdjustment(value.value)}
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  value={optionCapOverageAdjustment}
                  onBlur={e => {
                    setDirty(true);
                    setOptionCapOverageAdjusted(
                      handleAdjusted(
                        buildDetails?.upgradeCapOverage?.defaultValue,
                        Number(optionCapOverageAdjustment)
                      )
                    );
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} mb={isPhone ? 0 : 1.75} className={classes.adjustedFields}>
                <Item label="" value={formatMoney(optionCapOverageAdjusted)} bold={isPhone} />
              </Grid>
            </Grid>

            <Grid container spacing={1} pt={0.5}>
              <Grid xs={12} sm={4} item>
                <div style={{ visibility: 'hidden', height: '100%' }} />
              </Grid>
              <Grid xs={12} sm={4} item className={classes.adjustmentFields}>
                <TextField
                  disabled={!isAllowedToEditAndSavePrices}
                  fullWidth
                  type="text"
                  variant="standard"
                  autoComplete="nope"
                  label="Reason For Adjustment"
                  name="reasonForAdjustment"
                  size="small"
                  value={reasonForAdjustment}
                  onChange={e => setReasonForAdjustment(e.target.value)}
                  onBlur={e => {
                    setDirty(true);
                  }}
                  style={{ maxWidth: '475px' }}
                />
              </Grid>
            </Grid>

            {salesIncentiveRepeaterFF && (
              <>
                {selectedIncentiveTypeValues.map((incentive, index) => (
                  <Grid container spacing={0.25} key={index} mt={0.5}>
                    <Grid xs={12} sm={4} item>
                      <div style={{ visibility: 'hidden', height: '100%' }} />
                    </Grid>
                    <Grid item xs={12} sm={2} className={classes.adjustmentFields}>
                      <SMBSelect
                        fullWidth
                        required={Number(incentiveAdjustment) > 0}
                        disabled={isLoadingIncentiveTypes}
                        name={`incentives-${index}`}
                        label="Incentive Type"
                        id={`incentives-${index}`}
                        value={incentive?.incentiveId?.toString() || ''}
                        onChange={({ target: { value } }) => {
                          const selectedIncentive = incentiveTypesData.find(
                            item => item.value === value
                          );
                          const updatedIncentive = {
                            ...incentive,
                            incentiveId: parseInt(value, 10),
                            name: selectedIncentive?.description || ''
                          };
                          setSelectedIncentiveTypeValues(prev =>
                            prev.map((item, i) => (i === index ? updatedIncentive : item))
                          );
                          setDirty(true);
                        }}
                      >
                        {incentiveTypesData.map((incentiveItem, i) => (
                          <MenuItem key={`${i}`} value={incentiveItem.value}>
                            {incentiveItem.description}
                          </MenuItem>
                        ))}
                      </SMBSelect>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <NumericFormat
                        fullWidth
                        valueIsNumericString
                        thousandSeparator
                        customInput={TextField}
                        size="small"
                        variant="standard"
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={incentive.incentiveAmount}
                        onValueChange={value => {
                          const updatedIncentive = {
                            ...incentive,
                            incentiveAmount: Number(value.value)
                          };
                          setSelectedIncentiveTypeValues(prev =>
                            prev.map((item, i) => (i === index ? updatedIncentive : item))
                          );
                          setDirty(true);
                        }}
                        inputProps={{
                          style: { textAlign: 'right', paddingTop: '12px' }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={1}>
                      <Button
                        color="error"
                        onClick={() => {
                          setSelectedIncentiveTypeValues(prev =>
                            prev.filter((_, i) => i !== index)
                          );
                          setDirty(true);
                        }}
                        fullWidth
                        size="small"
                        style={{ minHeight: '40px' }}
                        startIcon={<Close />}
                      >
                        Delete
                      </Button>
                    </Grid>
                  </Grid>
                ))}
                <Grid container spacing={0.25} pt={0.5}>
                  <Grid xs={12} sm={4} item>
                    <div style={{ visibility: 'hidden', height: '100%' }} />
                  </Grid>
                  <Grid xs={12} sm={2} item className={classes.adjustmentFields}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setSelectedIncentiveTypeValues(prev => [
                          ...prev,
                          { recordId: 0, incentiveId: 0, name: '', incentiveAmount: 0 }
                        ]);
                        setDirty(true);
                      }}
                    >
                      Add Incentive Type
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}

            {!salesIncentiveRepeaterFF && (
              <Grid container spacing={1} pt={0.5}>
                <Grid xs={12} sm={4} item>
                  <div style={{ visibility: 'hidden', height: '100%' }} />
                </Grid>
                <Grid xs={12} sm={4} item className={classes.adjustmentFields}>
                  <Autocomplete
                    multiple
                    disabled={!isAllowedToEditAndSavePrices}
                    id="incentiveTypes"
                    value={selectedIncentiveTypeValues.map(incentive => ({
                      value: incentive?.incentiveId?.toString(),
                      description: incentive?.name,
                      shorthand: incentive?.name
                    }))}
                    onChange={(event, newValue: any[]) => {
                      setSelectedIncentiveTypeValues(
                        newValue.map(item => ({
                          recordId: 0,
                          incentiveId: parseInt(item.value, 10),
                          name: item?.description,
                          incentiveAmount: 0
                        }))
                      );
                      setDirty(true);
                    }}
                    inputValue={inputSelectedSurveyRequirement}
                    onInputChange={(event, newInputValue) => {
                      setInputSelectedSurveyRequirement(newInputValue);
                    }}
                    options={incentiveTypesData}
                    getOptionLabel={option => option.description}
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option: any) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        disabled={isLoadingIncentiveTypes}
                        label={isLoadingIncentiveTypes ? 'Loading...' : 'Incentive Types'}
                        placeholder="Selected Incentive Types"
                        required
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid container mt={2} mb={2}>
            <Grid item container>
              <SMButton
                id="selections-details-link"
                color="secondary"
                disabled={isLoadingBuildDetails}
                variant="contained"
                startIcon={<Info />}
                asLink
                to={selectionDetailsLink}
              >
                {'Selection Details'}
              </SMButton>
              {allowedToViewApproveBuild && (
                <Tooltip
                  title={
                    selectionsComplete === false
                      ? 'To approve build, please complete Ten Day Options selections.'
                      : ''
                  }
                >
                  <div>
                    <LoadingButton
                      id="approve-button"
                      color="primary"
                      variant="contained"
                      onClick={() => handleApproveBuild()}
                      disabled={isLoadingBuildDetails || isApprovingBuild || !canApproveBuild}
                      loading={isApprovingBuild}
                      startIcon={<CheckBox />}
                    >
                      {buildButtonTitle}
                    </LoadingButton>
                  </div>
                </Tooltip>
              )}
              {isAllowedToEditAndSavePrices && (
                <Button
                  id="edit"
                  type="submit"
                  variant="contained"
                  disabled={!isDirty || isUpdatingContractAdjustment || requireIncentiveType}
                  color="primary"
                  startIcon={<Save />}
                  onClick={() => {
                    handleContractAdjustmentUpdates();
                  }}
                >
                  {'Save Adjustments'}
                </Button>
              )}
              {tenDayComplete && (
                <LoadingButton
                  loading={isRegenerating}
                  loadingPosition="start"
                  variant="contained"
                  color="secondary"
                  disabled={isRegenerating}
                  startIcon={<RestorePage />}
                  onClick={async () => {
                    regenerateDocs();
                  }}
                >
                  Regenerate
                </LoadingButton>
              )}
              {tenDayComplete && buildDetails?.salesContractingType === 'Digital' && (
                <LoadingButton
                  id="send-forSignatures-button"
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    history.push(
                      `/sales/contracting/contract-markup?buildId=${buildDetails?.buildId}&neighborhood=${buildDetails?.neighborhoodDescription}&lot=${buildDetails?.lotNumber}&buildDocumentType=${buildDocuments.FinalContractPacket}`
                    );
                  }}
                  disabled={isLoadingBuildDetails}
                  loading={false}
                  startIcon={<HistoryEdu />}
                >
                  Send for Signatures
                </LoadingButton>
              )}
            </Grid>
            {tenDayComplete && (
              <>
                <Divider
                  sx={{
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(1),
                    width: '100%'
                  }}
                />
                <Grid item container spacing={1}>
                  <Grid item xs={12} md={'auto'}>
                    <LoadingButton
                      id="addendum-g"
                      color="primary"
                      variant="contained"
                      onClick={fetchAddendumG}
                      disabled={isLoadingAddendumG}
                      loading={isLoadingAddendumG}
                      startIcon={<Download />}
                    >
                      Addendum G
                    </LoadingButton>
                  </Grid>
                  <Grid item xs={12} md={'auto'}>
                    <LoadingButton
                      id="final-price-contract"
                      color="primary"
                      variant="contained"
                      onClick={fetchFinalPriceContract}
                      disabled={isLoadingFinalPriceContract}
                      loading={isLoadingFinalPriceContract}
                      startIcon={<Download />}
                    >
                      Final Price Contract
                    </LoadingButton>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
          <BuildNotes buildId={buildId} />
        </>
      )}
    </Page>
  );
};

const Item = ({
  label,
  value,
  color,
  bold
}: {
  label: string;
  value: any;
  color?: string;
  bold?: boolean;
}) => {
  return (
    <Typography color={color ? color : 'primary'}>
      <Typography
        component="span"
        color={color ? color : 'primary'}
        fontWeight={bold ? 'bold' : 'normal'}
        marginRight={0.25}
      >
        {label}
      </Typography>
      <Typography
        color="secondary"
        component="span"
        fontWeight={bold && label !== 'Total Options' ? 'bold' : 'normal'}
      >
        {value}
      </Typography>
    </Typography>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  buildPriceDetails: {
    '& p': {
      display: 'flex',
      justifyContent: 'space-between'
    },
    gap: theme.spacing(0.5)
  },
  adjustmentFields: {
    '@media (max-width: 600px)': {
      textAlign: 'right'
    },
    '@media (min-width: 600px)': {
      marginLeft: '75px'
    },
    width: '100%'
  },
  optionsCapAdjustment: {
    '@media (min-width: 600px)': {
      marginLeft: '18px',
      marginTop: '10px'
    }
  },
  optionsCapAdjusted: {
    '@media (min-width: 600px)': {
      marginLeft: '20px',
      marginTop: '10px'
    }
  },
  adjustmentReason: {
    '@media (min-width: 600px)': {
      marginLeft: '25px'
    },
    marginTop: '-25px'
  },
  blackText: {
    color: 'black'
  },
  priceChangeText: {},
  updateButton: {
    paddingTop: '4px',
    paddingBottom: '4px',
    fontSize: '0.875rem',
    border: '1px solid #ccc',
    height: '30px',
    marginTop: '14px',
    marginLeft: '5px'
  },
  onlyAdjustments: {
    '@media (min-width: 600px)': {
      marginLeft: '256px'
    }
  },
  incentives: {
    '@media (min-width: 600px)': {
      marginLeft: '465px'
    }
  },
  adjustedIncentive: {
    '@media (min-width: 600px)': {
      paddingRight: '323px'
    }
  },
  adjustedFields: {
    display: 'flex',
    justifyContent: 'end'
  },
  columnHeader: {
    '@media (min-width: 600px)': {
      marginLeft: '25px',
      paddingRight: '25px'
    },
    '@media (min-width: 1200px)': {
      marginLeft: '50px',
      paddingRight: '50px'
    }
  },
  adjustmentsHeader: {
    '@media (min-width: 600px)': {
      marginLeft: '25px'
    },
    '@media (min-width: 1200px)': {
      marginLeft: '10px'
    }
  },
  noAdjustments: {
    '@media (min-width: 600px)': {
      marginLeft: '75px'
    }
  },
  marginTop: {
    marginTop: '20px'
  }
}));
